/**
 * Pure queries over the plan topology (TD §4): who uses a corner or a road dot, what may be removed.
 * They work on committed docs and on immer drafts alike.
 */
import type { AnimalArea, Area, AreaId, NodeId, PlanDoc, Pt, RoadId, VertId } from '../../shared/types';

/** A closed ring of corners: an area's, or the zoo boundary's. */
export type RingRef = { area: AreaId } | 'plot';

/** Smallest number of distinct corners an area or boundary may have (R12). */
export const MIN_RING_VERTS = 3;
/** Smallest number of distinct dots an open road may have (R12). A loop needs MIN_RING_VERTS. */
export const MIN_ROAD_NODES = 2;

/** True for habitats, aviaries and aquariums. */
export function isAnimalArea(a: Area): a is AnimalArea {
  return a.kind === 'habitat' || a.kind === 'aviary' || a.kind === 'aquarium';
}

/** The corner list of a ring, or undefined if it doesn't exist. */
export function ringIds(doc: PlanDoc, ring: RingRef): VertId[] | undefined {
  return ring === 'plot' ? doc.plot?.verts : doc.areas[ring.area]?.verts;
}

/** Every ring (areas and the boundary) that uses this corner. */
export function ringsUsingVert(doc: PlanDoc, vertId: VertId): RingRef[] {
  const out: RingRef[] = [];
  if (doc.plot?.verts.includes(vertId)) out.push('plot');
  for (const [id, a] of Object.entries(doc.areas)) if (a.verts.includes(vertId)) out.push({ area: id });
  return out;
}

/** Ids of the areas that use this corner. */
export function areasUsingVert(doc: PlanDoc, vertId: VertId): AreaId[] {
  return Object.keys(doc.areas).filter(id => doc.areas[id]?.verts.includes(vertId));
}

/** True when more than one ring uses the corner (a shared wall or a meeting point). */
export function isSharedVert(doc: PlanDoc, vertId: VertId): boolean {
  return ringsUsingVert(doc, vertId).length > 1;
}

/** Ids of the roads that use this dot. */
export function roadsUsingNode(doc: PlanDoc, nodeId: NodeId): RoadId[] {
  return Object.keys(doc.roads).filter(id => doc.roads[id]?.nodes.includes(nodeId));
}

/** True when the dot is a junction: used by more than one road. */
export function isJunction(doc: PlanDoc, nodeId: NodeId): boolean {
  return roadsUsingNode(doc, nodeId).length > 1;
}

/** Fewest distinct dots the road may keep: 3 for a loop, 2 otherwise. */
export function minRoadNodes(closed: boolean): number {
  return closed ? MIN_RING_VERTS : MIN_ROAD_NODES;
}

/** Why the corner can't be removed (R12), or null if it can. */
export function whyCantRemoveVert(doc: PlanDoc, vertId: VertId): string | null {
  if (!doc.verts[vertId]) return 'That corner no longer exists.';
  for (const ring of ringsUsingVert(doc, vertId)) {
    const ids = ringIds(doc, ring) ?? [];
    if (new Set(ids.filter(v => v !== vertId)).size < MIN_RING_VERTS) {
      return ring === 'plot' ? 'The boundary needs at least 3 corners.' : 'An area needs at least 3 corners.';
    }
  }
  return null;
}

/** Why the dot can't be removed (R12), or null if it can. */
export function whyCantRemoveNode(doc: PlanDoc, nodeId: NodeId): string | null {
  if (!doc.nodes[nodeId]) return 'That dot no longer exists.';
  for (const rid of roadsUsingNode(doc, nodeId)) {
    const r = doc.roads[rid];
    if (r && new Set(r.nodes.filter(n => n !== nodeId)).size < minRoadNodes(r.closed)) {
      return r.closed ? 'A loop needs at least 3 dots.' : 'A road needs at least 2 dots.';
    }
  }
  return null;
}

/** The road's dot positions in order. */
export function roadNodePoints(doc: PlanDoc, roadId: RoadId): Pt[] {
  const r = doc.roads[roadId];
  if (!r) return [];
  return r.nodes.flatMap(id => { const n = doc.nodes[id]; return n ? [[n.x, n.y] as Pt] : []; });
}

/** Pools that belong to an animal area. */
export function poolsOf(doc: PlanDoc, areaId: AreaId): AreaId[] {
  return Object.keys(doc.areas).filter(id => { const a = doc.areas[id]; return !!a && 'parent' in a && a.parent === areaId; });
}
